import type { MutableRefObject } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import * as PIXI from 'pixi.js';
import { useQueryClient } from '@tanstack/react-query';
import { MapObject } from '../../models/mapObject';
import { AdminEditController } from './controller/admin/AdminEditController';
import {
  type AdminRadialMenuDeps,
} from './admin/adminRadialMenu';
import { attachIslandGodPointerHandlers } from './admin/islandGodHandlers';
import { setupAdminDragAndDrop } from './admin/adminDragAndDrop';

// ── Admin map-editing interaction cluster ──
// Composition root only: it wires MapCanvas's state setters and callbacks into the four
// admin-editing units and hands the results back. The units themselves live in ./admin:
//
//   adminRadialMenu.ts    — the long-press action wheel (options, placement, hit test)
//   islandGodHandlers.ts  — island double-tap (create object) / long-press (wheel)
//   adminDragAndDrop.ts   — the drag / resize gesture on a map object
//   adminOutline.ts       — the green selection + drag chrome both of the above draw
//   adminObjectPersist.ts — the two backend writes that end a gesture
//   controller/admin/AdminEditController.ts — dispatch of a chosen radial action
//
// NOTE on the params below: 17 members is over the grouping budget this decomposition
// works to, but every one of them is destructured at MapCanvas's call site. Regrouping
// them means editing MapCanvas, which is out of scope for this step.

export interface UseMapAdminInteractionParams {
  sceneRef: MutableRefObject<import('./mapSceneState').MapSceneState>;
  // React state setters
  setPendingMapObjectCreation: (value: { x: number; y: number } | null) => void;
  /** Opens the MapObjectTypeSelectionModal (useMapModalStore.openModal('typeSelection')). */
  openMapObjectTypeSelectionModal: () => void;
  setAdminRadialMenuPosition: Dispatch<SetStateAction<{ x: number; y: number }>>;
  setAdminRadialMenuMapObject: Dispatch<SetStateAction<{ mapObject: MapObject; marker: PIXI.Container } | null>>;
  setShowAdminRadialMenu: Dispatch<SetStateAction<boolean>>;
  setHighlightedAdminAction: Dispatch<SetStateAction<string | null>>;
  setAdminRadialObjectMode: Dispatch<SetStateAction<'dnd' | 'resize'>>;
  setAdminResizeDims: Dispatch<SetStateAction<{ w: number; h: number } | null>>;
  // Callbacks that remain in MapCanvas (used both inside and outside the cluster)
  clearAdminSelection: () => void;
  closeAdminRadialMenu: () => void;
  getAdminRadialPositions: (count: number) => Array<{ xOffset: number; yOffset: number }>;
  scheduleDeleteGuardClear: (mapObjectId: number) => void;
  // Constants owned by MapCanvas (also consumed outside the cluster)
  MOVE_THRESHOLD_TOUCH: number;
  MOVE_THRESHOLD_MOUSE: number;
  REAL_ESTATE_FIXED_SIZES: Record<number, { width: number; height: number }>;
  PLOT_MARKER_SIZE: { width: number; height: number };
  STATIC_OBJECT_FIXED_SIZES: Record<string, { width: number; height: number }>;
}

export interface UseMapAdminInteractionResult {
  attachIslandGodPointerHandlers: (container: PIXI.Container, island: MapObject) => void;
  executeAdminRadialAction: (actionKey: string, mapObject: MapObject, marker: PIXI.Container) => void;
  setupAdminDragAndDrop: (mapObject: MapObject, marker: PIXI.Container, event: PIXI.FederatedPointerEvent) => void;
}

export function useMapAdminInteraction({
  sceneRef,
  setPendingMapObjectCreation,
  openMapObjectTypeSelectionModal,
  setAdminRadialMenuPosition,
  setAdminRadialMenuMapObject,
  setShowAdminRadialMenu,
  setHighlightedAdminAction,
  setAdminRadialObjectMode,
  setAdminResizeDims,
  clearAdminSelection,
  closeAdminRadialMenu,
  getAdminRadialPositions,
  scheduleDeleteGuardClear,
  MOVE_THRESHOLD_TOUCH,
  MOVE_THRESHOLD_MOUSE,
  REAL_ESTATE_FIXED_SIZES,
  PLOT_MARKER_SIZE,
  STATIC_OBJECT_FIXED_SIZES,
}: UseMapAdminInteractionParams): UseMapAdminInteractionResult {
  const queryClient = useQueryClient();

  const radialMenu: AdminRadialMenuDeps = {
    sceneRef,
    getAdminRadialPositions,
    setAdminRadialMenuPosition,
    setAdminRadialMenuMapObject,
    setShowAdminRadialMenu,
    setHighlightedAdminAction,
    setAdminRadialObjectMode,
  };

  // AdminEditController (Command pattern, map-architecture Phase 7): dispatches
  // the radial action to a per-action Command object instead of an if/else
  // string-dispatch. Constructed fresh on every hook call (this hook body itself
  // re-runs every MapCanvas render), mirroring how `executeAdminRadialAction` was
  // previously just a fresh closure each render — no behavior change.
  // sceneRef is resolved only inside command execute (handlers), not during render.
  const adminEditController = new AdminEditController({
    sceneRef,
    setAdminResizeDims,
    closeAdminRadialMenu,
    scheduleDeleteGuardClear,
    queryClient,
    REAL_ESTATE_FIXED_SIZES,
    PLOT_MARKER_SIZE,
    STATIC_OBJECT_FIXED_SIZES,
  });
  const executeAdminRadialAction = adminEditController.execute;

  return {
    attachIslandGodPointerHandlers: (container, island) =>
      attachIslandGodPointerHandlers({
        sceneRef,
        radialMenu,
        clearAdminSelection,
        setPendingMapObjectCreation,
        openMapObjectTypeSelectionModal,
      }, container, island),

    executeAdminRadialAction,

    setupAdminDragAndDrop: (mapObject, marker, event) =>
      setupAdminDragAndDrop({
        sceneRef,
        radialMenu,
        executeAdminRadialAction,
        clearAdminSelection,
        closeAdminRadialMenu,
        setAdminResizeDims,
        moveThreshold: { touch: MOVE_THRESHOLD_TOUCH, mouse: MOVE_THRESHOLD_MOUSE },
        queryClient,
      }, mapObject, marker, event),
  };
}
